import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import AppDialog from "components/common/AppDialog";
import PdfFilesDropzone, { type UploadItem } from "components/pdfTools/PdfFilesDropzone";
import { formatBytes } from "components/preview/previewTypes";
import { trackPdfSplitJob } from "features/pdfSplitJobsSlice";
import {
  useCreatePdfJobMutation,
  useFinalizePdfUploadMutation,
  useGetPdfToolsConfigQuery,
  usePresignPdfUploadMutation,
  type PdfActiveJobRef,
  type PdfOptionSchemaProperty,
  type PdfSplitterInfo,
} from "features/pdfTools/pdfTools";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import useAppNavigate from "hooks/useAppNavigate";
import {
  apiErrorText,
  parseSplitConcurrencyError,
  splitProvidersOf,
  splittersForVendor,
} from "./splitJobUi";
import SplitBusySlotsAlert from "./SplitBusySlotsAlert";
import { splitJobPath } from "./splitRoutes";

export interface NewSplitJobDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  userLabel: string;
  majorJob: { id: number; name: string; vendor: string };
  /** Why the operator may only look; the dialog then never uploads. */
  readOnlyReason?: string | null;
}

const ALLOWED_EXTENSIONS = [".zip", ".pdf"];

function fileExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx).toLowerCase() : "";
}

function schemaDefaults(splitter: PdfSplitterInfo | null): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  const properties = splitter?.options_schema?.properties ?? {};
  for (const [name, property] of Object.entries(properties)) {
    if (property.default !== undefined) defaults[name] = property.default;
    else if (property.type === "boolean") defaults[name] = false;
    else defaults[name] = "";
  }
  return defaults;
}

/**
 * A new split job in a split major job: the vendor's splitters only, the
 * client's archive or PDFs uploaded straight to storage, then the job page.
 */
export default function NewSplitJobDialog({
  open,
  onClose,
  userId,
  userLabel,
  majorJob,
  readOnlyReason = null,
}: NewSplitJobDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { data: config, isLoading: configLoading } = useGetPdfToolsConfigQuery(
    undefined,
    { skip: !open },
  );
  const [presignUpload] = usePresignPdfUploadMutation();
  const [finalizeUpload] = useFinalizePdfUploadMutation();
  const [createJob] = useCreatePdfJobMutation();

  const [splitterKey, setSplitterKey] = useState("");
  const [optionValues, setOptionValues] = useState<Record<string, unknown>>({});
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [items, setItems] = useState<UploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [formError, setFormError] = useState("");
  const [busySlots, setBusySlots] = useState<{
    message: string;
    activeJobs: PdfActiveJobRef[];
  } | null>(null);

  const splitters = useMemo(
    () => splittersForVendor(config?.splitters, majorJob.vendor),
    [config, majorJob.vendor],
  );
  const splitter = splitters.find((s) => s.key === splitterKey) ?? null;
  const schemaProperties = useMemo(
    () =>
      Object.entries(splitter?.options_schema?.properties ?? {}) as [
        string,
        PdfOptionSchemaProperty,
      ][],
    [splitter],
  );
  const requiredOptions = splitter?.options_schema?.required ?? [];
  const limits = config?.limits;

  // One splitter for the vendor: nothing to choose.
  useEffect(() => {
    if (!open || splitterKey || splitters.length !== 1) return;
    setSplitterKey(splitters[0].key);
    setOptionValues(schemaDefaults(splitters[0]));
  }, [open, splitterKey, splitters]);

  const resetForm = () => {
    setSplitterKey("");
    setOptionValues({});
    setShowSecret({});
    setItems([]);
    setSubmitting(false);
    setUploadedCount(0);
    setFormError("");
    setBusySlots(null);
  };

  useEffect(() => {
    if (!submitting) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [submitting]);

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleSplitterChange = (key: string) => {
    setSplitterKey(key);
    const next = splitters.find((s) => s.key === key) ?? null;
    setOptionValues(schemaDefaults(next));
  };

  const setOption = (name: string, value: unknown) => {
    setOptionValues((prev) => ({ ...prev, [name]: value }));
  };

  const warn = (message: string) =>
    dispatch(pushNotification({ message, severity: "warning" }));

  const handleAddFiles = (files: File[]) => {
    setFormError("");
    setItems((prev) => {
      const next = [...prev];
      for (const file of files) {
        if (!ALLOWED_EXTENSIONS.includes(fileExtension(file.name))) {
          warn(`"${file.name}" is not a ZIP or PDF file.`);
          continue;
        }
        if (file.size === 0) {
          warn(`"${file.name}" is empty.`);
          continue;
        }
        if (limits && file.size > limits.max_file_bytes) {
          warn(
            `"${file.name}" exceeds the ${formatBytes(limits.max_file_bytes)} per-file limit.`,
          );
          continue;
        }
        if (next.some((i) => i.file.name === file.name)) {
          warn(`"${file.name}" is already in the list.`);
          continue;
        }
        if (limits && next.length >= limits.max_files_per_job) {
          warn(`At most ${limits.max_files_per_job} files are allowed per job.`);
          break;
        }
        next.push({ file, status: "queued" });
      }
      return next;
    });
  };

  const handleRemoveFile = (name: string) => {
    setItems((prev) => prev.filter((i) => i.file.name !== name));
  };

  const setItemState = (name: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((i) => (i.file.name === name ? { ...i, ...patch } : i)));
  };

  const requiredFilled = requiredOptions.every((name) => {
    const value = optionValues[name];
    return typeof value === "string" ? value.trim().length > 0 : value != null;
  });

  const canSubmit =
    !readOnlyReason &&
    !submitting &&
    !!splitter &&
    requiredFilled &&
    items.length > 0;

  const handleSubmit = async () => {
    // The raw upload below bypasses the API's read-only guard: refuse here.
    if (!canSubmit || !splitter || readOnlyReason) return;
    setSubmitting(true);
    setFormError("");
    setBusySlots(null);
    setUploadedCount(items.filter((i) => i.status === "uploaded").length);

    try {
      const uploaded: { s3_key: string; file_name: string }[] = [];
      for (const item of items) {
        if (item.status === "uploaded" && item.s3Key) {
          uploaded.push({ s3_key: item.s3Key, file_name: item.file.name });
          continue;
        }
        setItemState(item.file.name, { status: "uploading", error: undefined });
        try {
          const presigned = await presignUpload({
            file_name: item.file.name,
            content_type: item.file.type || "application/octet-stream",
            user_id: userId,
          }).unwrap();
          const putRes = await fetch(presigned.upload_url, {
            method: "PUT",
            headers: presigned.headers,
            body: item.file,
          });
          if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
          const finalized = await finalizeUpload({
            s3_key: presigned.s3_key,
            user_id: userId,
          }).unwrap();
          setItemState(item.file.name, { status: "uploaded", s3Key: finalized.s3_key });
          uploaded.push({ s3_key: finalized.s3_key, file_name: item.file.name });
          setUploadedCount((n) => n + 1);
        } catch (err: unknown) {
          const message =
            (err as { data?: unknown })?.data !== undefined
              ? apiErrorText(err, "Upload failed.")
              : "Upload failed - check your connection and try again.";
          setItemState(item.file.name, { status: "failed", error: message });
          setFormError(`Could not upload "${item.file.name}". ${message}`);
          setSubmitting(false);
          return;
        }
      }

      const job = await createJob({
        major_job_id: majorJob.id,
        user_id: userId,
        splitter_key: splitter.key,
        options: optionValues,
        inputs: uploaded,
      }).unwrap();

      dispatch(
        trackPdfSplitJob({
          jobId: job.job_id,
          label: `${userLabel} - ${splitter.label}`,
          userId,
          majorJobId: majorJob.id,
        }),
      );
      dispatch(
        pushNotification({ message: `Split job started for ${userLabel}.`, severity: "success" }),
      );
      resetForm();
      onClose();
      navigate(splitJobPath(userId, majorJob.id, job.job_id));
    } catch (err: unknown) {
      const busy = parseSplitConcurrencyError(err);
      if (busy) {
        // The files stay uploaded: Try again starts the job without them.
        setBusySlots({ message: busy.message, activeJobs: busy.activeJobs });
      } else {
        setFormError(apiErrorText(err, "Could not start the split job. Please try again."));
      }
      setSubmitting(false);
    }
  };

  const totalToUpload = items.length;
  const providers = splitProvidersOf(config);

  const renderOptionField = (name: string, property: PdfOptionSchemaProperty) => {
    const label = property.title || name;
    const required = requiredOptions.includes(name);
    if (property.type === "boolean") {
      return (
        <Box key={name}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(optionValues[name])}
                onChange={(e) => setOption(name, e.target.checked)}
                disabled={submitting}
              />
            }
            label={label}
          />
          {property.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: -0.5 }}
            >
              {property.description}
            </Typography>
          )}
        </Box>
      );
    }
    const isSecret = property.format === "password" || property.writeOnly;
    return (
      <TextField
        key={name}
        size="small"
        label={required ? `${label} *` : label}
        type={isSecret && !showSecret[name] ? "password" : "text"}
        value={String(optionValues[name] ?? "")}
        onChange={(e) => setOption(name, e.target.value)}
        disabled={submitting}
        helperText={property.description}
        autoComplete={isSecret ? "new-password" : undefined}
        InputProps={
          isSecret
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setShowSecret((prev) => ({ ...prev, [name]: !prev[name] }))
                      }
                      aria-label={`Toggle ${label} visibility`}
                    >
                      {showSecret[name] ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    );
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="New split job"
      maxWidth="sm"
      showCloseIcon={!submitting}
      actions={
        <>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            title={readOnlyReason ?? undefined}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? "Starting..." : busySlots ? "Try again" : "Start splitting"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          For <b>{userLabel}</b> in <b>{majorJob.name}</b>. The result is reviewed here
          first, then saved to the client&apos;s {majorJob.vendor} data folder in Data
          Storage.
        </Typography>

        {configLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : splitters.length === 0 ? (
          <Alert severity="warning">
            No splitter reads {majorJob.vendor || "this vendor"}&apos;s documents. Splitters
            exist for: {providers.join(", ") || "none"}.
          </Alert>
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Splitter *"
              value={splitterKey}
              onChange={(e) => handleSplitterChange(e.target.value)}
              disabled={submitting}
              helperText="The document format to split. Each splitter knows its own options."
            >
              {splitters.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            {schemaProperties.map(([name, property]) => renderOptionField(name, property))}

            <PdfFilesDropzone
              items={items}
              onAddFiles={handleAddFiles}
              onRemove={handleRemoveFile}
              disabled={submitting || !!readOnlyReason}
            />

            {submitting && totalToUpload > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (uploadedCount / totalToUpload) * 100)}
                  sx={{ borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Uploading file {Math.min(uploadedCount + 1, totalToUpload)} of{" "}
                  {totalToUpload}...
                </Typography>
              </Box>
            )}

            {busySlots && (
              <SplitBusySlotsAlert
                message={busySlots.message}
                activeJobs={busySlots.activeJobs}
                note="Your files are uploaded and kept here - press Try again when one of these finishes."
              />
            )}

            {formError && <Alert severity="error">{formError}</Alert>}
          </>
        )}
      </Box>
    </AppDialog>
  );
}
